import { useMemo, useState } from "react";
import { getraenke } from "@/lib/getraenke";
import { trackSettled } from "@/lib/track";

const de = (v) => (Number(v) || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 });

function NumField({ label, value, onChange, suffix }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input type="number" inputMode="numeric" min={0} value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 outline-none" />
        <span className="px-3 text-sm text-muted-foreground">{suffix}</span>
      </div>
    </label>
  );
}

function Slider({ label, value, onChange, hint }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium flex justify-between"><span>{label}</span><span className="text-muted-foreground">{value} %</span></span>
      <input type="range" min={0} max={100} step={5} value={value}
        onChange={(e) => onChange(Number(e.target.value))} className="accent-[hsl(173_58%_39%)]" />
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Zeile({ label, liter, einheit }) {
  if (liter <= 0) return null;
  return (
    <div className="flex items-center justify-between border-t py-2 first:border-t-0">
      <span className="text-sm">{label}</span>
      <span className="text-sm font-medium">{de(liter)} L · {einheit}</span>
    </div>
  );
}

export default function GetraenkeRechner() {
  const [gaeste, setGaeste] = useState(30);
  const [stunden, setStunden] = useState(5);
  const [alkoholAnteil, setAlkoholAnteil] = useState(70);
  const [bierAnteil, setBierAnteil] = useState(60);

  const res = useMemo(
    () => getraenke({ gaeste, stunden, alkoholAnteil, bierAnteil, puffer: 10 }),
    [gaeste, stunden, alkoholAnteil, bierAnteil]
  );
  const upd = () => trackSettled("getraenke_berechnet", { calculator: "party" });

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Gäste" value={gaeste} onChange={(v) => { setGaeste(v); upd(); }} suffix="Pers." />
            <NumField label="Dauer" value={stunden} onChange={(v) => { setStunden(v); upd(); }} suffix="Std." />
          </div>
          <Slider label="Anteil Alkoholtrinker" value={alkoholAnteil} onChange={(v) => { setAlkoholAnteil(v); upd(); }} />
          <Slider label="davon Bier statt Wein" value={bierAnteil} onChange={(v) => { setBierAnteil(v); upd(); }} hint="Rest trinkt Wein." />
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="text-sm text-muted-foreground">Einkaufsliste für {res.gaeste} Gäste (inkl. 10 % Puffer)</div>
            <div className="text-2xl font-semibold mt-0.5" data-testid="getraenke-ergebnis">{de(res.gesamtLiter)} L gesamt</div>
            <div className="mt-2">
              <Zeile label="Bier" liter={res.bier.liter} einheit={`${res.bier.flaschen} Fl. (${res.bier.kisten} Kästen)`} />
              <Zeile label="Wein" liter={res.wein.liter} einheit={`${res.wein.flaschen} Flaschen`} />
              <Zeile label="Sekt (Empfang)" liter={res.sekt.liter} einheit={`${res.sekt.flaschen} Flaschen`} />
              <Zeile label="Wasser" liter={res.wasser.liter} einheit={`${res.wasser.flaschen} Flaschen (1 L)`} />
              <Zeile label="Softdrinks / Säfte" liter={res.softdrinks.liter} einheit={`${res.softdrinks.flaschen} Flaschen (1 L)`} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Richtwerte für eine mehrstündige Feier, bitte verantwortungsvoll und nach Anlass anpassen. Sekt ist als einmaliger Empfang gerechnet, die übrigen Mengen skalieren mit der Dauer.
          </p>
        </div>
      </div>
    </div>
  );
}
